import React, { useEffect, useState, useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import API_BASE from "../config";

// ── ProjectsListPage ───────────────────────────────────────────────────
// Top-level landing for the Signify Sports Tool 2.0. Two stacked tables:
// active projects on top, the Archive section collapsed below.
//
// Columns: Name / Projektnummer / Stadt / Priorität / Letzte Berechnung /
// Aktionen. Every column except Aktionen is sortable (click the header).
// Actions per row: open (→ /projects/:id by clicking the name link),
// edit metadata (pencil), and archive ↔ unarchive toggle. Delete is
// intentionally absent — archive is the soft-delete UX so a fat-finger
// doesn't lose work.
//
// 2026-05-21 (round 2): added "Letzte Berechnung" column +
// sortable-table pattern reused from ProjectDetailPage. Backend
// list_projects now returns `last_calculation_at` (ISO ts or null).
// ───────────────────────────────────────────────────────────────────────

// Priority pill — maps the backend's enum to a translated label + color.
// Returns null when there's nothing worth showing (default "normal" or
// missing value) so the column stays clean for legacy projects.
const PriorityPill = ({ value }) => {
  if (!value || value === "normal") return <span style={{ color: "var(--text-disabled)" }}>—</span>;
  const variants = {
    urgent: { className: "pill pill-fail", label: "Dringend" },
    high:   { className: "pill pill-warn", label: "Hoch" },
    low:    { className: "pill pill-neutral", label: "Niedrig" },
  };
  const v = variants[value] || { className: "pill pill-neutral", label: value };
  return <span className={v.className}>{v.label}</span>;
};

// Compact date renderer for the table — dd.mm.yyyy. Returns "—" for
// null/undefined so the cell stays aligned. Time intentionally
// dropped 2026-05-21 to keep the column narrow on smaller viewports
// (added back via tooltip below if the precise moment matters).
const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch (_) { return iso; }
};
// Full timestamp shown in the cell's tooltip so the user can still
// recover the exact time on hover if they need it.
const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
};

// Numeric priority for sorting — urgent on top desc, then high, normal, low.
// Used by the sort comparator so the column can sort sensibly instead of
// alphabetically (which would put "high" before "normal" before "urgent").
const PRIORITY_RANK = { urgent: 3, high: 2, normal: 1, low: 0 };

const ProjectsListPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  // 2026-05-21: shared sort state for both active + archive tables. Default
  // is "most-recently-calculated first" — the projects that the user is
  // most likely working with float to the top.
  const [sort, setSort] = useState({ key: "last_calculation_at", dir: "desc" });
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/projects?include_archived=true`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setProjects(await res.json());
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  // Comparator factory — returns a (a, b) sort function for the current
  // sort key + direction. Nulls always go to the END regardless of
  // direction so "no value" never beats real data in either order.
  const compareBy = (key, dir) => {
    const dirFactor = dir === "asc" ? 1 : -1;
    const pick = (p) => {
      switch (key) {
        case "project_name":     return (p.project_name || "").toLowerCase();
        case "project_number":   return (p.project_number || "").toLowerCase();
        case "project_town":     return (p.project_town || "").toLowerCase();
        case "priority":         return PRIORITY_RANK[p.priority] ?? -1;
        case "last_calculation_at": return p.last_calculation_at || null;
        default:                 return 0;
      }
    };
    return (a, b) => {
      const av = pick(a), bv = pick(b);
      // Nulls / empty strings → always sink to the bottom.
      const aEmpty = av === null || av === "" || av === undefined;
      const bEmpty = bv === null || bv === "" || bv === undefined;
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      if (typeof av === "string") return av.localeCompare(bv) * dirFactor;
      return (av - bv) * dirFactor;
    };
  };

  // Split into active vs archive, each sorted by the current sort key.
  const { active, archive } = useMemo(() => {
    const cmp = compareBy(sort.key, sort.dir);
    const a = [], b = [];
    for (const p of projects) (p.archived ? b : a).push(p);
    a.sort(cmp);
    b.sort(cmp);
    return { active: a, archive: b };
  }, [projects, sort]);

  // Click handler for sortable column headers. Toggles direction on
  // re-click of the same column; switches to a new column with a
  // direction default that "makes sense" per column type:
  //   - dates: desc (newest first)
  //   - text columns: asc (alphabetical)
  //   - priority: desc (urgent first)
  const onHeader = (key) => {
    setSort((prev) => {
      if (prev.key === key) {
        return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      }
      const desc = key === "last_calculation_at" || key === "priority";
      return { key, dir: desc ? "desc" : "asc" };
    });
  };

  // Visual sort marker (▲/▼) shown next to the active sort column.
  const sortMarker = (key) =>
    sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";

  const setArchived = async (id, archived) => {
    try {
      const res = await fetch(
        `${API_BASE}/projects/${id}/${archived ? "archive" : "unarchive"}`,
        { method: "POST" }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      await load();
    } catch (e) {
      console.error("archive toggle failed", e);
    }
  };

  // Single table row used by both sections (active + archive).
  const Row = ({ p, archived }) => (
    <tr>
      {/* Name cell allows wrap on long names — overrides the global
          .data-table td { overflow: hidden; text-overflow: ellipsis }
          rule so we never lose project-name info. The other 5
          columns keep ellipsis truncation since they hold short
          values (numbers, dates, pills, icons). */}
      <td style={{ whiteSpace: "normal", overflow: "visible" }}>
        <Link
          to={`/projects/${p.id}`}
          style={{ color: "var(--text-strong)", textDecoration: "none", fontWeight: 600 }}
        >
          {p.project_name || `Projekt ${p.id}`}
        </Link>
        {p.project_type && (
          <span className="pill pill-neutral" style={{ marginLeft: 8 }}>
            {p.project_type}
          </span>
        )}
      </td>
      <td
        style={{
          color: "var(--text-muted)",
          fontFamily: "var(--font-mono)",
          fontSize: 12,
        }}
      >
        {p.project_number || "—"}
      </td>
      <td>{p.project_town || "—"}</td>
      <td><PriorityPill value={p.priority} /></td>
      <td
        className="num"
        title={fmtDateTime(p.last_calculation_at)}
        style={{
          color: p.last_calculation_at ? "var(--text-body)" : "var(--text-disabled)",
        }}
      >
        {fmtDate(p.last_calculation_at)}
      </td>
      <td className="actions">
        <button
          className="btn btn-icon"
          onClick={() => navigate(`/projects/${p.id}/edit`)}
          title="Metadaten bearbeiten"
          aria-label="Metadaten bearbeiten"
        >✎</button>
        <button
          className="btn btn-icon"
          onClick={() => setArchived(p.id, !archived)}
          title={archived ? "Aus Archiv wiederherstellen" : "Archivieren"}
          aria-label={archived ? "Wiederherstellen" : "Archivieren"}
        >{archived ? "↩" : "🗄"}</button>
      </td>
    </tr>
  );

  // Re-usable card wrapping the table. Body uses .flush so the table
  // can paint its own padding (cells are already padded).
  // Headers carry the .sortable class so .data-table styles them as
  // clickable (cursor + hover color), and the onClick toggles the sort.
  const ProjectsCard = ({ title, subtitle, rows, archived }) => (
    <div className="report-card">
      <div className="report-card-header">
        <h3>{title}</h3>
        {subtitle && <div className="subtitle">{subtitle}</div>}
      </div>
      <div className="report-card-body flush">
        <table className="data-table">
          {/* Explicit column widths (sum 100%) so the browser doesn't
              auto-blow any column to fit content. Combined with
              table-layout: fixed in App.css this GUARANTEES all 6
              columns fit horizontally without scroll, regardless of
              the longest project name or date string. */}
          <colgroup>
            <col style={{ width: "32%" }} />{/* Name + type pill */}
            <col style={{ width: "14%" }} />{/* Nr. */}
            <col style={{ width: "20%" }} />{/* Stadt */}
            <col style={{ width: "10%" }} />{/* Prio. */}
            <col style={{ width: "12%" }} />{/* Berechnet */}
            <col style={{ width: "12%" }} />{/* Aktionen */}
          </colgroup>
          <thead>
            <tr>
              {/* Headers kept short so the browser's auto column-sizer
                  doesn't blow up a column just to fit a long label.
                  "Projektnummer" → "Nr.", "Letzte Berechnung" → "Berechnet". */}
              <th className="sortable" onClick={() => onHeader("project_name")}>
                Name{sortMarker("project_name")}
              </th>
              <th className="sortable" onClick={() => onHeader("project_number")}>
                Nr.{sortMarker("project_number")}
              </th>
              <th className="sortable" onClick={() => onHeader("project_town")}>
                Stadt{sortMarker("project_town")}
              </th>
              <th className="sortable" onClick={() => onHeader("priority")}>
                Prio.{sortMarker("priority")}
              </th>
              <th className="sortable" onClick={() => onHeader("last_calculation_at")}>
                Berechnet{sortMarker("last_calculation_at")}
              </th>
              <th style={{ textAlign: "right" }}>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((p) => <Row key={p.id} p={p} archived={archived} />)}
          </tbody>
        </table>
      </div>
    </div>
  );

  // KPI strip data — derived from the loaded project list. Memo'd so
  // adding/removing a project re-counts without rerunning the loader.
  // "Dringend" tile removed 2026-05-21 (Philip: meist 0, nur visuelles
  // Rauschen). Falls je wieder gebraucht: project.priority === "urgent".
  const kpis = useMemo(() => {
    return {
      active: active.length,
      archived: archive.length,
      total: projects.length,
    };
  }, [projects, active, archive]);

  return (
    <div style={{ padding: "16px 20px", minHeight: "100%" }}>
      <div
        className="section-title"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          // flex-wrap lets the "+ Neues Projekt" button drop below the
          // title on narrow viewports instead of getting clipped at the
          // right edge (Philip's screenshot 2026-05-21).
          flexWrap: "wrap",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <div>
          <h2>Projekte</h2>
          <div className="subtitle">
            Übersicht aller Sportplatz-Berechnungen
          </div>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/projects/new")}
          style={{ flexShrink: 0 }}
        >
          + Neues Projekt
        </button>
      </div>

      {/* ── KPI strip ─────────────────────────────────────────────────
          Skill recommendation for data-dense dashboards: surface key
          metrics at the top so the user gets the state of the world
          before scrolling into the table. Numbers use mono/tabular
          figures so they line up visually across tiles. */}
      {!loading && !error && (
        <div className="kpi-strip">
          <div className="kpi-tile">
            <div className="kpi-tile-label">Gesamt</div>
            <div className="kpi-tile-value">{kpis.total}</div>
            <div className="kpi-tile-sub">Alle Projekte (inkl. Archiv)</div>
          </div>
          <div className="kpi-tile is-pass">
            <div className="kpi-tile-label">Aktiv</div>
            <div className="kpi-tile-value">{kpis.active}</div>
            <div className="kpi-tile-sub">In Bearbeitung</div>
          </div>
          <div className="kpi-tile is-mute">
            <div className="kpi-tile-label">Archiv</div>
            <div className="kpi-tile-value">{kpis.archived}</div>
            <div className="kpi-tile-sub">Soft-gelöscht</div>
          </div>
        </div>
      )}

      {loading && (
        <div className="empty-state">Lade Projekte…</div>
      )}
      {error && (
        <div className="banner banner-fail">Fehler beim Laden: {error}</div>
      )}

      {!loading && !error && (
        <>
          {active.length === 0 ? (
            <div className="empty-state">
              Noch keine aktiven Projekte. Über „Neues Projekt" anlegen.
            </div>
          ) : (
            <ProjectsCard
              title={`Aktive Projekte (${active.length})`}
              subtitle="Sportplätze in Bearbeitung"
              rows={active}
              archived={false}
            />
          )}

          {archive.length > 0 && (
            <div style={{ marginTop: "var(--space-8)" }}>
              <button
                type="button"
                onClick={() => setShowArchive((v) => !v)}
                style={{
                  background: "none", border: "none", cursor: "pointer",
                  padding: 0, font: "inherit", color: "var(--text-muted)",
                  display: "inline-flex", alignItems: "center", gap: 8,
                  marginBottom: "var(--space-3)",
                }}
                aria-expanded={showArchive}
              >
                <span style={{
                  fontSize: "var(--type-lg)", fontWeight: 700,
                  color: "var(--text-muted)",
                }}>
                  {showArchive ? "▾" : "▸"} Archiv
                </span>
                <span className="pill pill-archived">{archive.length}</span>
              </button>
              {showArchive && (
                <ProjectsCard
                  title={`Archivierte Projekte (${archive.length})`}
                  subtitle="Wiederherstellbar"
                  rows={archive}
                  archived={true}
                />
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProjectsListPage;
